import {
  NgDocRootComponent,
  NgDocNavbarComponent,
  NgDocSidebarComponent,
  NgDocThemeService,
} from '@ng-doc/app';
import { Component, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { NgIf } from '@angular/common';
import {
  NgDocButtonIconComponent,
  NgDocIconComponent,
  NgDocSmoothResizeComponent,
  NgDocTooltipDirective,
  preventInitialChildAnimations,
} from '@ng-doc/ui-kit';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    NgDocRootComponent,
    NgDocNavbarComponent,
    NgDocSidebarComponent,
    NgIf,
    NgDocButtonIconComponent,
    NgDocIconComponent,
    NgDocSmoothResizeComponent,
    NgDocTooltipDirective,
  ],
  animations: [preventInitialChildAnimations],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'acium-docs';
  currentTheme: any;
  logo: string = 'assets/img/ACIUM-L-logo.svg';
  logoLabel = environment.branding.logoLabel;
  leftContent: any;
  currentYear = new Date().getFullYear();
  consoleTheme: any;
  isNotIframe = false;

  private readonly LOGO_PATHS = {
    acium: {
      dark: 'assets/img/ACIUM-D-logo.svg',
      light: 'assets/img/ACIUM-L-logo.svg'
    },
    itopia: {
      dark: 'assets/img/itopia-D-logo.svg',
      light: 'assets/img/itopia-L-logo.svg'
    },
    dizzion: {
      dark: 'https://storage.googleapis.com/ebm-assets-prod/assets/logos/dizzion/Logo_Dark.svg',
      light: 'https://storage.googleapis.com/ebm-assets-prod/assets/logos/dizzion/Logo_Dark.svg',
    },
  } as const;

  private readonly THEME_MAP = {
    acium: {
      light: 'acium-light',
      dark: 'acium-dark'
    },
    itopia: {
      light: 'itopia-light',
      dark: 'itopia-dark'
    },
    dizzion: {
      light: 'dizzion-light',
      dark: 'dizzion-dark'
    },
  } as const;

  constructor(
    protected readonly themeService: NgDocThemeService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
  ) {
    this.themeService.themeChanges().subscribe(() => {
      this.currentTheme = this.themeService.currentTheme;
      this.setLogo();
    });
    this._router.events
      .pipe(filter((val) => val instanceof NavigationEnd))
      .subscribe(() => {
        const queryParams = this._activatedRoute.snapshot.queryParams;
        const currentUrl = this._router.url;

        if (queryParams['console-theme']) {
          this.enableConsoleTheme();
        } else {
          switch (currentUrl) {
            case '/':
              this._router.navigate(['/acium-docs-overview']);
              break;
            case '/error':
              this.isNotIframe = true;
              break;
            default:
              this.checkIframe();
              break;
          }
        }
      });
  }

  ngOnInit(): void {
    this.checkIframe();
    this.setCurrentTheme();
  }

  checkIframe() {
    if (environment.chekIframeNavigation) {
       this.isNotIframe = window.top === window.self && !window.location.href.includes('localhost');      
    }
    else {
      this.isNotIframe = false // Allow all navigation 
    }   
    
    if (this.isNotIframe) {
      this._router.navigate(['/error']);
    }
  }

  setCurrentTheme() {
    const brand = environment.branding.theme as keyof typeof this.THEME_MAP;
    const themeId = this.THEME_MAP[brand]['light'];
    this.themeService.set(themeId);
    document.documentElement.style.setProperty('--ng-doc-primary', environment.branding.primaryColor);
    this.setLogo();
  }

  setLogo() {
    const brand = environment.branding.theme as keyof typeof this.LOGO_PATHS;
    const theme = this.isDarkTheme() ? 'dark' : 'light';
    this.logo = this.LOGO_PATHS[brand][theme];
  }

  setTheme(): void {
    const brand = environment.branding.theme as keyof typeof this.THEME_MAP;
    const newTheme = this.THEME_MAP[brand][this.isDarkTheme() ? 'light' : 'dark'];
    this.themeService.set(newTheme);
  }

  

  isDarkTheme(): boolean {
    return this.currentTheme?.id?.includes('dark') ?? false;
  }

  enableConsoleTheme(): void {
    const brand = environment.branding.theme as keyof typeof this.THEME_MAP;
    this.consoleTheme = this._activatedRoute.snapshot.queryParams['console-theme'];
    const theme = this.consoleTheme?.includes('dark') ? 'dark' : 'light';
    this.themeService.set(this.THEME_MAP[brand][theme]);
    this.setLogo();
    this._router.navigate(['/acium-docs-overview']);
  }
}
